/**
 * Typed errors for the wallet module.
 *
 * Every failure mode that a caller may legitimately want to branch on gets its own class.
 * The most important one is {@link WrongPasswordError}: unlocking a vault is a purely local
 * operation (AES-GCM authentication), there is no server round-trip that could tell the UI
 * "bad password", so the crypto layer is the only place that knows.
 */

/** Base class for every error thrown by the wallet module. */
export class WalletError extends Error {
  public readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = new.target.name;
    // Required when targeting ES5: extending built-ins breaks the prototype chain otherwise.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** The password did not decrypt the vault (AES-GCM authentication failed). */
export class WrongPasswordError extends WalletError {
  constructor(message = 'Wrong password') {
    super('WRONG_PASSWORD', message);
  }
}

/** The BIP-39 phrase is not a valid mnemonic (bad word, bad length or bad checksum). */
export class InvalidMnemonicError extends WalletError {
  constructor(message = 'Invalid recovery phrase') {
    super('INVALID_MNEMONIC', message);
  }
}

/** A key (or a raw byte string that should have been a key) is malformed. */
export class InvalidKeyError extends WalletError {
  constructor(message = 'Invalid key material') {
    super('INVALID_KEY', message);
  }
}

/** Some caller-supplied argument is malformed (account id, hex, base64...). */
export class InvalidInputError extends WalletError {
  constructor(message = 'Invalid input') {
    super('INVALID_INPUT', message);
  }
}

/** A login challenge did not carry the mandatory `nuxaris-login:` prefix. */
export class InvalidNonceError extends WalletError {
  constructor(message = 'Refusing to sign a login challenge without the nuxaris-login: prefix') {
    super('INVALID_NONCE', message);
  }
}

/** The base64 blob handed to `signPreparedTx` is not a 32-byte Canton transaction hash. */
export class InvalidPreparedTxError extends WalletError {
  constructor(message = 'Prepared transaction hash must be exactly 32 raw bytes') {
    super('INVALID_PREPARED_TX', message);
  }
}

/** No vault stored on this device for that account. */
export class VaultNotFoundError extends WalletError {
  constructor(accountId: string) {
    super('VAULT_NOT_FOUND', `No wallet vault stored on this device for account ${accountId}`);
  }
}

/** A vault already exists for that account; overwriting would destroy key material. */
export class VaultAlreadyExistsError extends WalletError {
  constructor(accountId: string) {
    super(
      'VAULT_ALREADY_EXISTS',
      `A wallet vault already exists for account ${accountId}. Pass { overwrite: true } to replace it.`,
    );
  }
}

/** The stored vault is corrupt, truncated, or written by a newer version of this module. */
export class CorruptVaultError extends WalletError {
  constructor(message = 'Stored wallet vault is corrupt or unreadable') {
    super('CORRUPT_VAULT', message);
  }
}

/** The session has been locked (or was never unlocked); its key material is gone. */
export class SessionLockedError extends WalletError {
  constructor(message = 'Wallet session is locked') {
    super('SESSION_LOCKED', message);
  }
}

/** `crypto.subtle` is unavailable (non-secure context, or a JS runtime without WebCrypto). */
export class CryptoUnavailableError extends WalletError {
  constructor(
    message = 'WebCrypto (crypto.subtle) is unavailable. A secure context (HTTPS or localhost) is required.',
  ) {
    super('CRYPTO_UNAVAILABLE', message);
  }
}

/** No persistent storage backend (no IndexedDB, and none injected via `setVaultStorage`). */
export class StorageUnavailableError extends WalletError {
  constructor(
    message = 'No vault storage available: IndexedDB is missing and no storage backend was injected.',
  ) {
    super('STORAGE_UNAVAILABLE', message);
  }
}

/** The password is below the module's minimum length. */
export class WeakPasswordError extends WalletError {
  constructor(minLength: number) {
    super('WEAK_PASSWORD', `Password must be at least ${minLength} characters long`);
  }
}
